import { FunctionComponent, MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { faUsers } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { GlobalSearchModal } from "@/components/GlobalSearchModal/GlobalSearchModal";

import { extractDomain } from "@/utils/extractDomain";

import { Organization } from "../../../Apollo/schema/get-people";

interface CompanyFindMoreLeadsProps {
	organization?: Organization;
	/** Display name for the company, overriding {@link Organization.name} when set (e.g. a tracker's own label). */
	companyName?: string;
	/**
	 * Extension override. When provided, the button delegates to this handler (the Chrome extension
	 * opens its native in-panel find-people view) instead of opening the web {@link GlobalSearchModal},
	 * which relies on MUI/portal behavior that doesn't reuse into the shadow root. Omit on the web.
	 */
	onFindMoreLeads?: (company: { name?: string; domain?: string }) => void;
}

/**
 * Full-width "Find more leads at {company}" button beneath the company card (unified-prototype v33
 * `lp-company-actions`). Opens the company-scoped people search — the global search on its filters
 * tab with this company's domain pre-applied — so the rep can discover more people at the account.
 * Requires a resolvable company domain (the search filters on it); renders nothing without one.
 */
export const CompanyFindMoreLeads: FunctionComponent<CompanyFindMoreLeadsProps> = ({
	organization,
	companyName,
	onFindMoreLeads,
}) => {
	const { t } = useTranslation();
	const [showSearch, setShowSearch] = useState(false);

	const domain = organization?.primaryDomain || extractDomain(organization?.websiteUrl);
	const name = companyName || organization?.name || "";

	if (!domain) {
		return null;
	}

	const handleClick = (event: MouseEvent) => {
		// Hosts that treat a card click as selection (the feed run card) must not
		// see this as a body click.
		event.stopPropagation();

		if (onFindMoreLeads) {
			onFindMoreLeads({ name, domain });

			return;
		}

		setShowSearch(true);
	};

	return (
		<>
			<button
				className="mt-2 flex h-[38px] w-full cursor-pointer items-center justify-center gap-2 rounded-[10px] border border-gray-200 bg-white px-3 text-sm font-semibold text-text duration-200 hover:shadow-md"
				data-testid="company-find-more-leads"
				type="button"
				onClick={handleClick}
			>
				<Icon className="w-4" icon={faUsers} />
				<span className="truncate">
					{t("contacts.leadPanel.actions.findMoreLeads", { company: name })}
				</span>
			</button>

			{!onFindMoreLeads && showSearch && (
				<GlobalSearchModal
					initialFilterCompany={{ name, domain }}
					isOpened={showSearch}
					setIsOpened={setShowSearch}
				/>
			)}
		</>
	);
};
